'use client';
import { useRouter } from 'next/navigation';
import React, { useCallback } from 'react';
import { useCurrentPath } from 'src/hooks/useCurrentPath';
import { useAppDispatch, useAppSelector } from 'src/store/client/config';
import { modalActions } from 'src/store/client/slices/modalSlice';
import { Bounce } from 'src/styles/animation';
import styled, { css } from 'styled-components';

interface LikeButtonProps {
  isActive: boolean;
  isAnimate: boolean;
  onClick: (e: React.MouseEvent<HTMLElement>) => unknown;
  children?: React.ReactNode;
}

const LikeButton = ({ isActive, isAnimate, onClick, children }: LikeButtonProps) => {
  const router = useRouter();
  const currentPath = useCurrentPath();
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();

  const WrappedHandler = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (!user.LoggedIn) {
        dispatch(
          modalActions.openConfirmModal({
            icon: 'danger',
            title: ['로그인 이후에 이용 가능합니다.'],
            closeText: ['취소'],
            confirmText: ['로그인'],
            onConfirm: () => {
              router.push(`/account/login?retUrl=${currentPath}`);
            },
          }),
        );
        return;
      } else {
        onClick(event);
      }
    },
    [dispatch, onClick, user.LoggedIn],
  );

  return (
    <Button onClick={WrappedHandler} aria-label={isActive ? '좋아요 취소' : '좋아요'}>
      <Svg
        $isAnimate={isAnimate}
        xmlns="http://www.w3.org/2000/svg"
        width="30"
        height="30"
        viewBox="0 0 30 30"
        fill="none"
      >
        <Path
          $isActive={isActive}
          d="M18.587 8.75C21.2286 8.75 22.5 10.3837 22.5 12.5556C22.5 16.954 15.1333 21.25 15 21.25C14.8667 21.25 7.5 16.954 7.5 12.5556C7.5 10.3837 8.77138 8.75 11.413 8.75C12.9297 8.75 14.425 10.7804 15 11.4054C15.575 10.7804 17.0703 8.75 18.587 8.75Z"
          stroke="#7B7B7B"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </Svg>
      {children}
    </Button>
  );
};

const Button = styled.button`
  display: flex;
  gap: 2px;
  align-items: center;

  &.animated svg {
    animation: ${Bounce} 0.9s both;
  }

  &.active svg path {
    fill: ${({ theme }) => theme.rootColor.red2};
    stroke: ${({ theme }) => theme.rootColor.red2};
  }
`;

const Svg = styled.svg<{ $isAnimate: boolean }>`
  animation: ${({ $isAnimate }) =>
    $isAnimate
      ? css`
          ${Bounce} 0.9s both
        `
      : 'none'};
`;

const Path = styled.path<{ $isActive: boolean }>`
  fill: ${({ theme, $isActive }) => ($isActive ? theme.rootColor.red2 : 'none')};
  stroke: ${({ theme, $isActive }) => ($isActive ? theme.rootColor.red2 : '#DFDFDF')};
`;

export default LikeButton;
